import api from "./api";

export type TarefaStatus = "pendente" | "em_andamento" | "parada" | "concluida";
export type TarefaTipo = "tarefa" | "evento";

export type TarefaRecorrenciaFreq =
  | "none"
  | "daily"
  | "weekly"
  | "monthly"
  | "yearly"
  | "weekdays";

export interface TarefaRecorrencia {
  freq: TarefaRecorrenciaFreq;
  interval: number;
  until: string | null;
}

export type TarefaDeleteEscopo = "esta" | "desta_em_diante" | "todas";

export interface TarefaNotificacoes {
  manha7h: boolean;
  antes30min: boolean;
  aoSalvar: boolean;
}

export const DEFAULT_TAREFA_NOTIFICACOES: TarefaNotificacoes = {
  manha7h: true,
  antes30min: true,
  aoSalvar: true,
};

export interface TarefaGrupoDto {
  id: string;
  nome: string;
  categoria: "tipo" | "regiao";
  criadorId: string;
  createdAt: string;
}

export type TarefaTimelineEventType =
  | "criada"
  | "editada"
  | "status_alterado"
  | "compartilhamento_alterado"
  | "concluida";

export interface TarefaTimelineEvent {
  id: string;
  type: TarefaTimelineEventType;
  at: string;
  userId: string;
  userName: string;
  note?: string;
}

export interface TarefaDto {
  id: string;
  tipo: TarefaTipo;
  titulo: string;
  descricao: string;
  data: string;
  horario: string | null;
  horarioFim: string | null;
  grupoId: string | null;
  notificacoes: TarefaNotificacoes;
  status: TarefaStatus;
  criadorId: string;
  criadorName: string;
  usuarioIds: string[];
  usuarioNomes: string[];
  timeline: TarefaTimelineEvent[];
  serieId?: string | null;
  recorrencia?: TarefaRecorrencia | null;
  masterId?: string;
  ocorrenciaData?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TarefaUsuarioOption {
  id: string;
  displayName: string;
  username: string;
  hasPhone: boolean;
}

export interface TarefaFormValues {
  tipo: TarefaTipo;
  titulo: string;
  descricao: string;
  data: string;
  horario: string;
  horarioFim: string;
  grupoId: string;
  notificacoes: TarefaNotificacoes;
  status: TarefaStatus;
  usuarioIds: string[];
  recorrencia: TarefaRecorrencia | null;
}

export const EMPTY_RECORRENCIA: TarefaRecorrencia | null = null;

export const EMPTY_TAREFA_FORM: TarefaFormValues = {
  tipo: "tarefa",
  titulo: "",
  descricao: "",
  data: "",
  horario: "",
  horarioFim: "",
  grupoId: "",
  notificacoes: { ...DEFAULT_TAREFA_NOTIFICACOES },
  status: "pendente",
  usuarioIds: [],
  recorrencia: null,
};

export const TAREFA_STATUS_LABELS: Record<TarefaStatus, string> = {
  pendente: "Pendente",
  em_andamento: "Em andamento",
  parada: "Parada",
  concluida: "Concluída",
};

export const TAREFA_TIMELINE_LABELS: Record<TarefaTimelineEventType, string> = {
  criada: "Tarefa criada",
  editada: "Tarefa editada",
  status_alterado: "Status alterado",
  compartilhamento_alterado: "Compartilhamento alterado",
  concluida: "Tarefa concluída",
};

export async function fetchTarefaUsuarios(): Promise<TarefaUsuarioOption[]> {
  const { data } = await api.get<{ success: boolean; users: TarefaUsuarioOption[] }>(
    "/api/tarefas/usuarios",
  );
  return data.users;
}

export async function listTarefas(filters?: {
  data?: string;
  status?: TarefaStatus;
}): Promise<TarefaDto[]> {
  const params = new URLSearchParams();
  if (filters?.data) params.set("data", filters.data);
  if (filters?.status) params.set("status", filters.status);
  const qs = params.toString();
  const { data } = await api.get<{ success: boolean; tasks: TarefaDto[] }>(
    `/api/tarefas${qs ? `?${qs}` : ""}`,
  );
  return data.tasks.map((t) => ({
    ...t,
    tipo: t.tipo ?? "tarefa",
  }));
}

export async function createTarefa(values: TarefaFormValues): Promise<TarefaDto> {
  const { data } = await api.post<{ success: boolean; task: TarefaDto }>("/api/tarefas", {
    tipo: values.tipo,
    titulo: values.titulo,
    descricao: values.descricao,
    data: values.data,
    horario: values.horario || null,
    horarioFim: values.horarioFim || null,
    grupoId: values.grupoId || null,
    notificacoes: values.notificacoes,
    status: values.status,
    usuarioIds: values.usuarioIds,
    recorrencia: values.recorrencia,
  });
  const task = data.task;
  return { ...task, tipo: task.tipo ?? values.tipo };
}

export async function updateTarefa(
  id: string,
  values: Partial<TarefaFormValues>,
): Promise<TarefaDto> {
  const { data } = await api.patch<{ success: boolean; task: TarefaDto }>(
    `/api/tarefas/${id}`,
    {
      ...values,
      horario: values.horario !== undefined ? values.horario || null : undefined,
      horarioFim: values.horarioFim !== undefined ? values.horarioFim || null : undefined,
      grupoId: values.grupoId !== undefined ? values.grupoId || null : undefined,
    },
  );
  const task = data.task;
  const fallbackTipo = values.tipo ?? task.tipo ?? "tarefa";
  return { ...task, tipo: task.tipo ?? fallbackTipo };
}

export async function fetchTarefaGrupos(): Promise<TarefaGrupoDto[]> {
  const { data } = await api.get<{ success: boolean; groups: TarefaGrupoDto[] }>(
    "/api/tarefas/grupos",
  );
  return data.groups;
}

export async function createTarefaGrupo(
  nome: string,
  categoria: "tipo" | "regiao",
): Promise<TarefaGrupoDto> {
  const { data } = await api.post<{ success: boolean; group: TarefaGrupoDto }>(
    "/api/tarefas/grupos",
    { nome, categoria },
  );
  return data.group;
}

export async function deleteTarefaGrupo(id: string): Promise<void> {
  await api.delete(`/api/tarefas/grupos/${id}`);
}

export async function deleteTarefa(
  id: string,
  opts?: { escopo?: TarefaDeleteEscopo; ocorrenciaData?: string },
): Promise<void> {
  const params = new URLSearchParams();
  if (opts?.escopo) params.set("escopo", opts.escopo);
  if (opts?.ocorrenciaData) params.set("ocorrenciaData", opts.ocorrenciaData);
  const qs = params.toString();
  await api.delete(`/api/tarefas/${encodeURIComponent(id)}${qs ? `?${qs}` : ""}`);
}

export function tarefaToFormValues(tarefa: TarefaDto): TarefaFormValues {
  return {
    tipo: tarefa.tipo ?? "tarefa",
    titulo: tarefa.titulo,
    descricao: tarefa.descricao,
    data: tarefa.data,
    horario: tarefa.horario ?? "",
    horarioFim: tarefa.horarioFim ?? "",
    grupoId: tarefa.grupoId ?? "",
    notificacoes: tarefa.notificacoes ?? { manha7h: true, antes30min: true, aoSalvar: true },
    status: tarefa.status,
    usuarioIds: tarefa.usuarioIds ?? [],
    recorrencia: tarefa.recorrencia ?? null,
  };
}

export function isTarefaCompartilhada(tarefa: TarefaDto, currentUserId: string): boolean {
  return (
    !!currentUserId &&
    tarefa.criadorId !== currentUserId &&
    tarefa.usuarioIds.includes(currentUserId)
  );
}

export function primaryAssigneeName(tarefa: TarefaDto, currentUserId?: string): string {
  if (currentUserId && isTarefaCompartilhada(tarefa, currentUserId)) {
    return tarefa.criadorName || "Criador";
  }
  if (tarefa.usuarioNomes?.length > 0) return tarefa.usuarioNomes[0];
  return tarefa.criadorName || "Você";
}
